import React, { lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';

// Comps
const ProductGalleryTwoColumn = lazy(
  () => import('@project-stories/ecommerce/Product/ProductGallery/Layouts/TwoColumn')
);
const ProductGalleryStacked = lazy(() => import('@project-stories/ecommerce/Product/ProductGallery/Layouts/Stacked'));
const ProductGalleryCarousel = lazy(() => import('@project-stories/ecommerce/Product/ProductGallery/Layouts/Carousel'));
import ProductGalleryCarouselWithThumbnail from '@project-stories/ecommerce/Product/ProductGallery/Layouts/CarouselWithThumbnail';

// Hooks
import useMediaQuery from '@src/hooks/useMediaQuery';

// Types
import { ILiquidMedia } from '@src/types/product';

// Stylings
import './ProductGallery.scss';

const Layouts = {
  'two-column': ProductGalleryTwoColumn,
  stacked: ProductGalleryStacked,
  carousel: ProductGalleryCarousel,
  'carousel-with-thumbnail': ProductGalleryCarouselWithThumbnail
};

type Props = {
  media: ILiquidMedia[];
  settings: any;
  sectionId?: string; // Add new prop
};

export function reorderMedia(media: ILiquidMedia[], initialIndex: number) {
  if (
    !Array.isArray(media) ||
    media.length === 0 ||
    initialIndex < 0 ||
    initialIndex >= media.length
  ) {
    return media;
  }
  return [media[initialIndex], ...media.slice(0, initialIndex), ...media.slice(initialIndex + 1)];
}


export const ProductGallery = ({ media, settings, sectionId }: Props) => {
  const { gallery_type, gallery_type_mobile } = settings.section;

  const galleryType = media.length === 1 ? 'stacked' : gallery_type;
  const galleryTypeMobile = media.length === 1 ? 'stacked' : gallery_type_mobile;

  const LayoutComponent = Layouts[galleryType];
  const MobileLayoutComponent = Layouts[galleryTypeMobile];

  // Hooks
  const isMobile = useMediaQuery(undefined, 1023);

  const mobileGalleryId = `mobile-gallery-target${sectionId ? `-${sectionId}` : ''}`;
  const mobileGalleryTarget = document.getElementById(mobileGalleryId);

  return (
    <Suspense>
      {!isMobile ? (
        <LayoutComponent
          media={media}
          isMobile={isMobile}
          settings={settings}
        />
      ) : mobileGalleryTarget ? (
        createPortal(
          <MobileLayoutComponent
            media={media}
            isMobile={isMobile}
            settings={settings}
          />,
          mobileGalleryTarget
        )
      ) : (
        <MobileLayoutComponent
          media={media}
          isMobile={isMobile}
          settings={settings}
        />
      )}
    </Suspense>
  );
};
